import { cn } from '@/components/ui/cn';
import { MarkdownMessage } from '@/features/chat/MarkdownMessage';
import type { AttachmentRef } from '@/lib/api/types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Documents attached to this message when it was sent (spec 0015). */
  attachments?: AttachmentRef[];
}

/** Animated "assistant is typing" dots. Decorative: hidden from assistive tech
 * (status is announced via the live region in ChatPage) and static under
 * prefers-reduced-motion. */
function TypingIndicator() {
  return (
    <span
      aria-hidden="true"
      data-testid="typing-indicator"
      className="inline-flex items-center gap-1 py-1"
    >
      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s] motion-reduce:animate-none" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s] motion-reduce:animate-none" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 motion-reduce:animate-none" />
    </span>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  streaming: boolean;
}

export function MessageBubble({ message, streaming }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const attachments = message.attachments ?? [];
  return (
    <li className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2 text-sm',
          isUser ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-900',
        )}
      >
        {/* Sender label sits OUTSIDE the testid node so screen readers get
            context without changing the message element's text content. */}
        <span className="sr-only">{isUser ? 'You said: ' : 'Assistant said: '}</span>
        <div
          data-testid={isUser ? 'user-message' : 'assistant-message'}
          // User messages stay verbatim (whitespace-pre-wrap preserves what they
          // typed); assistant messages render as Markdown (spec 0016), which owns
          // its own whitespace and soft-break handling.
          className={cn(isUser && 'whitespace-pre-wrap')}
        >
          {message.content ? (
            <>
              {isUser ? message.content : <MarkdownMessage content={message.content} />}
              {/* Blinking caret while this message is the one actively
                  streaming. Empty + aria-hidden so it adds no accessible or
                  asserted text, and static under prefers-reduced-motion. */}
              {!isUser && streaming && (
                <span
                  aria-hidden="true"
                  data-testid="assistant-caret"
                  className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 animate-pulse rounded-full bg-current align-text-bottom motion-reduce:animate-none"
                />
              )}
            </>
          ) : !isUser && streaming ? (
            <TypingIndicator />
          ) : null}
        </div>
        {attachments.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {attachments.map((att) => (
              <li key={att.id}>
                <span
                  data-testid="message-attachment"
                  className={cn(
                    'inline-flex max-w-[16rem] items-center gap-1 truncate rounded px-2 py-0.5 text-xs',
                    isUser ? 'bg-brand-800 text-white' : 'bg-gray-200 text-gray-700',
                  )}
                >
                  <span className="sr-only">Attachment: </span>
                  <span className="truncate">{att.filename}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
